import {
  runPublishFileRecordWorkflow,
  runShareFileRecordWorkflow,
} from "@/action-dialog";
import { useAddFileRecordToLibrary } from "@/api/fileRecords";
import { useToggleFavorite } from "@/api/resourceTags";
import type {
  ActionScope,
  ObjectActionHandlers,
  ObjectActionId,
} from "@/object-actions/types";
import { resolveFileRecordShowPublish } from "@/panes/file-record-detail/publish";
import { RESOURCE_TAG_FAVORITE, type FileRecord } from "@cohesive/api-client";
import { useCallback, useMemo } from "react";
import toast from "react-hot-toast";

async function copyFileRecordUrlToClipboard(url: string): Promise<void> {
  await navigator.clipboard.writeText(url);
  toast.success("URL copied to clipboard");
}

/**
 * Public viewer URL for a published file, or undefined when the file has
 * never been published. The backend stores the raw CloudFront URL
 * (`{host}/public/{id}`); the user-facing viewer lives under
 * `/view/public/{id}`, so we inject `/view` here — mirrors FilePublishButton.
 */
function resolveFileRecordPublicUrl(record: FileRecord): string | undefined {
  if (!record.published || !record.published_url) return undefined;
  return record.published_url.replace("/public/", "/view/public/");
}

export interface FileRecordActionHandlerDeps {
  onOpen?: (record: FileRecord) => void;
  onRename?: (record: FileRecord) => void;
  onCopy?: (record: FileRecord) => void;
  onDelete?: (record: FileRecord) => void;
  /** Hide the file's canvas node (non-destructive; sets `node.hidden`). */
  onHideOnCanvas?: (record: FileRecord) => void;
  /** Un-hide the file's canvas node. */
  onShowOnCanvas?: (record: FileRecord) => void;
  /** Registry scope — gates publish on shared-with-me / favorites. */
  actionScope?: ActionScope;
}

/**
 * Every action id createFileRecordActionHandlers can supply. The return
 * literal is typed against this list so the two cannot drift, and the
 * deliverability test checks the manifests only offer ids some layer
 * actually handles — the resolver silently DROPS handler-less actions,
 * which is how Copy to once shipped as nothing at all.
 */
export const FILE_RECORD_HANDLED_ACTION_IDS = [
  "star",
  "unstar",
  "open",
  "rename",
  "move",
  "copy-to",
  "duplicate",
  "share",
  "copy-url",
  "publish",
  "download",
  "in-library",
  "add-to-library",
  "delete",
  "remove-from-canvas",
  "show-on-canvas",
] as const satisfies readonly ObjectActionId[];

type FileRecordHandledActionID =
  (typeof FILE_RECORD_HANDLED_ACTION_IDS)[number];

export function createFileRecordActionHandlers(
  record: FileRecord,
  deps: FileRecordActionHandlerDeps,
  toggleFavorite: ReturnType<typeof useToggleFavorite>,
  addToLibraryAtRoot: (record: FileRecord) => void,
): Record<FileRecordHandledActionID, (() => void) | undefined> {
  const isFavorite = !!record.tags?.includes(RESOURCE_TAG_FAVORITE);

  return {
    star:
      record.id && !isFavorite
        ? () =>
            toggleFavorite.mutate({
              resourceType: "file-record",
              resourceId: record.id,
              isFavorite,
            })
        : undefined,
    unstar:
      record.id && isFavorite
        ? () =>
            toggleFavorite.mutate({
              resourceType: "file-record",
              resourceId: record.id,
              isFavorite,
            })
        : undefined,
    open: deps.onOpen ? () => deps.onOpen?.(record) : undefined,
    rename: deps.onRename
      ? () => window.setTimeout(() => deps.onRename?.(record), 0)
      : undefined,
    // Move and Copy to are submenu-only — the destination picker attached by
    // useAttachFileRecordTransferSubmenus IS the interaction, and every
    // surface the manifest offers them on hosts the picker. The placeholder
    // handlers exist because the resolver drops handler-less actions before
    // the submenu is ever attached (the bug that shipped Copy to as nothing
    // at all); supplying them here means no surface can forget.
    move: record.id ? () => {} : undefined,
    "copy-to": record.id ? () => {} : undefined,
    duplicate: deps.onCopy ? () => deps.onCopy?.(record) : undefined,
    share: record.id
      ? () =>
          window.setTimeout(
            () => void runShareFileRecordWorkflow({ record }),
            0,
          )
      : undefined,
    "copy-url": (() => {
      const publicUrl = resolveFileRecordPublicUrl(record);
      return publicUrl
        ? () => {
            void copyFileRecordUrlToClipboard(publicUrl).catch(() => {
              toast.error("Failed to copy URL");
            });
          }
        : undefined;
    })(),
    publish:
      record.id &&
      resolveFileRecordShowPublish({
        record,
        canEdit: record.can_edit !== false,
        scope: deps.actionScope,
      })
        ? () =>
            window.setTimeout(
              () => void runPublishFileRecordWorkflow({ record }),
              0,
            )
        : undefined,
    download:
      record.status === "processed" && !record.path?.endsWith("/")
        ? () => {}
        : undefined,
    // A3: same-id move into the workspace Library + a reference left behind.
    // Opens the folder-choosing dialog; the manifest hides this for Library
    // files, references, and folders.
    "in-library":
      record.class === "workspace" || record.kind === "link"
        ? () => {}
        : undefined,
    // Fallback for hosts that can't cascade (the canvas toolbar's utility
    // button): commit straight to the Library root — the backend resolves
    // the container's own workspace when none is sent (A3). Menus render the
    // folder-choosing cascade instead via useAttachAddToLibrarySubmenu.
    "add-to-library":
      record.id && record.class !== "workspace" && record.kind !== "link"
        ? () => addToLibraryAtRoot(record)
        : undefined,
    delete: deps.onDelete ? () => deps.onDelete?.(record) : undefined,
    "remove-from-canvas": deps.onHideOnCanvas
      ? () => deps.onHideOnCanvas?.(record)
      : undefined,
    "show-on-canvas": deps.onShowOnCanvas
      ? () => deps.onShowOnCanvas?.(record)
      : undefined,
  };
}

export function useFileRecordObjectActionHandlers(
  record: FileRecord,
  deps: FileRecordActionHandlerDeps,
): ObjectActionHandlers {
  const toggleFavorite = useToggleFavorite();
  const addToLibrary = useAddFileRecordToLibrary();
  const addToLibraryAtRoot = useCallback(
    (target: FileRecord) => {
      addToLibrary.mutate(
        { record: target },
        {
          onSuccess: () =>
            toast.success("Added to the Library — still available here"),
          onError: () => toast.error("Failed to add to the Library"),
        },
      );
    },
    [addToLibrary],
  );
  return useMemo(
    () =>
      createFileRecordActionHandlers(
        record,
        deps,
        toggleFavorite,
        addToLibraryAtRoot,
      ),
    [addToLibraryAtRoot, deps, record, toggleFavorite],
  );
}
